import React, { useEffect, useMemo, useRef, useState } from 'react';

import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import AddCategory from './components/AddCategory';
import SortableTreeItem from './dndComponents/SortableTreeItem';
import DndContextComponent from 'components/common/sortableDnD/DndContextComponent';

import { countRenderedLinks, formattedUpdateWeightBody } from 'utils/linkLibrary';
import { handleError } from 'utils/errorHandler';
import { LINK_LIBRARY_TYPES } from 'constants/linkLibrary';

import { useFetchQuickLinks, useUpdateLinkLibraryMutation } from 'hooks/query/useQuickLinksQuery';

import type { TriggerBtnProps } from './types';
import { LinkLibraryTreeNode } from 'interface/linkLibraryTree';

type LinkLike = {
  id?: number | string;
  name?: string;
  title?: string;
  type?: string;
  categoryId?: number | string;
  linkUrl?: string;
  link?: string | null;
  children?: LinkLike[];
  links?: LinkLike[];
  expanded?: boolean;
  isExpanded?: boolean;
  parentId?: number | string;
  level?: number;
};

const normalizeLinkRows = (links: LinkLike[] = [], categoryId?: number | string): LinkLibraryTreeNode[] => {
  return links.map((link, index) => ({
    ...link,
    id: link.id ?? `link-${categoryId ?? 'root'}-${index}`,
    name: link.name ?? link.title ?? '',
    type: LINK_LIBRARY_TYPES.LINK,
    categoryId: link.categoryId ?? categoryId,
    linkUrl: link.linkUrl ?? link.link ?? '',
  })) as LinkLibraryTreeNode[];
};

const normalizeTreeNode = (node: LinkLike, parentId?: number | string): LinkLibraryTreeNode => {
  const normalizedChildren = (node.children || []).map((child) => normalizeTreeNode(child, node.id ?? parentId));

  if (node.type === LINK_LIBRARY_TYPES.LINK) {
    return {
      ...node,
      id: node.id ?? `links-${node.categoryId ?? parentId ?? 'root'}`,
      name: node.name ?? node.title ?? '',
      type: LINK_LIBRARY_TYPES.LINK,
      categoryId: node.categoryId ?? parentId,
      children: normalizedChildren.length
        ? normalizedChildren
        : normalizeLinkRows(node.links || node.children || [], node.categoryId ?? parentId),
      linkUrl: node.linkUrl ?? node.link ?? undefined,
    } as LinkLibraryTreeNode;
  }

  const directLinks = normalizeLinkRows(node.links || [], node.id ?? parentId);
  const hasLinkGroup = normalizedChildren.some((child) => child.type === LINK_LIBRARY_TYPES.LINK && !child.linkUrl);

  if (directLinks.length && !hasLinkGroup) {
    return {
      ...node,
      id: node.id ?? `category-${parentId ?? 'root'}`,
      name: node.name ?? node.title ?? '',
      type: (node.type as LinkLibraryTreeNode['type']) || LINK_LIBRARY_TYPES.CATEGORY,
      parentId: node.parentId ?? parentId,
      children: [
        ...normalizedChildren,
        {
          id: `links-${node.id ?? parentId ?? 'root'}`,
          name: node.name ?? node.title ?? '',
          type: LINK_LIBRARY_TYPES.LINK as LinkLibraryTreeNode['type'],
          categoryId: node.id ?? parentId,
          children: directLinks,
        },
      ],
    } as LinkLibraryTreeNode;
  }

  return {
    ...node,
    id: node.id ?? `category-${parentId ?? 'root'}`,
    name: node.name ?? node.title ?? '',
    type: (node.type as LinkLibraryTreeNode['type']) || LINK_LIBRARY_TYPES.CATEGORY,
    parentId: node.parentId ?? parentId,
    children: normalizedChildren,
  } as LinkLibraryTreeNode;
};

const normalizeTreeData = (data: LinkLike[]): LinkLibraryTreeNode[] => data.map((node) => normalizeTreeNode(node));

const getNormalizedTreeData = (data: LinkLike[]): LinkLibraryTreeNode[] => normalizeTreeData(data);

const getTotalRenderedLinks = (data: LinkLibraryTreeNode[]): number => countRenderedLinks(data);

const LinkLibraryList: React.FC = () => {
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [activePopoverId, setActivePopoverId] = useState<string | null>(null);

  useEffect(() => {
    window.document.title = 'Link Libraries | Vyaguta';
  }, []);

  const AddCategoryTrigger: React.FC<TriggerBtnProps> = ({ onClick }) => (
    <button
      type="button"
      className="f-left add-employee-button"
      onClick={() => {
        setIsCategoryModalOpen(true);
        onClick();
      }}
    >
      Add new category
    </button>
  );

  const {
    data: quickLinksData,
    isLoading: isQuickLinksLoading,
    isError: isQuickLinksError,
    error: quickLinksError,
    errorUpdatedAt: quickLinksErrorUpdatedAt,
  } = useFetchQuickLinks<LinkLike[]>();
  const { mutate } = useUpdateLinkLibraryMutation();
  const lastHandledQuickLinksErrorAtRef = useRef(0);
  const normalizedQuickLinksData = useMemo(
    () => (quickLinksData ? getNormalizedTreeData(quickLinksData) : []),
    [quickLinksData]
  );
  const [draftState, setDraftState] = useState(() => ({
    sourceData: quickLinksData,
    treeData: normalizedQuickLinksData,
  }));
  const treeData = draftState.sourceData === quickLinksData ? draftState.treeData : normalizedQuickLinksData;
  const totalLinks = useMemo(() => getTotalRenderedLinks(treeData), [treeData]);

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
  };

  useEffect(() => {
    if (!isQuickLinksError || !quickLinksError || quickLinksErrorUpdatedAt <= lastHandledQuickLinksErrorAtRef.current) {
      return;
    }

    handleError(quickLinksError);
    lastHandledQuickLinksErrorAtRef.current = quickLinksErrorUpdatedAt;
  }, [isQuickLinksError, quickLinksError, quickLinksErrorUpdatedAt]);

  const onDragEnd = (data: LinkLibraryTreeNode[]) => {
    const bodyPayload = formattedUpdateWeightBody(data);

    setDraftState({ sourceData: quickLinksData, treeData: data });
    mutate(bodyPayload);
  };

  return (
    <>
      {!isQuickLinksLoading && (
        <>
          <div className="full-scope-card rst__header">
            <div className="full-scope-card__header table-header name-wrap">
              <div>
                <div className="table-title">Link Library</div>
                <div className="table-subtitle">{totalLinks} links in total</div>
              </div>
              <div className="table-header__right">
                <AddCategory
                  isEdit={false}
                  isSubCategory={false}
                  isOpen={isCategoryModalOpen}
                  onClose={closeCategoryModal}
                  TriggerBtn={AddCategoryTrigger}
                />
              </div>
            </div>
          </div>
          <div className="rst mbpx-30">
            <DndContextComponent listData={treeData} onDragEnd={onDragEnd}>
              <ul>
                <SortableContext items={treeData.map(({ id }) => id)} strategy={verticalListSortingStrategy}>
                  {treeData.map((data) => (
                    <div key={`${data.id}-${data.name}`}>
                      <SortableTreeItem
                        id={data.id}
                        data={data}
                        activePopoverId={activePopoverId}
                        setActivePopoverId={setActivePopoverId}
                      />
                    </div>
                  ))}
                </SortableContext>
              </ul>
            </DndContextComponent>
          </div>
        </>
      )}
    </>
  );
};

export default LinkLibraryList;
